import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Location, UPI } from '../../assests/ExportImageIcon'
import loadScript from '../../utils/loadScript'
import { showToast } from '../../componants/toast/toastService'
import { setHotelBookingConfirmation } from '../../redux/hotelSearch/hotelSearchSlice'
import { formatMoney } from '../../utils/formatMoney'
import { resolveAllowedGateways } from '../../utils/flightFare'
import HotelSearchLoader from '../../componants/hotelSearchLoader/HotelSearchLoader'
import {
  findStripePublishableKeyInPayload,
  getRazorpayKeyId,
  getStripePublishableKey,
} from '../../config/stripeEnv'

const GATEWAYS = [
  { key: 'razorpay', label: 'Razorpay', subtitle: 'UPI, Cards, NetBanking, Wallets', icon: UPI },
  { key: 'stripe', label: 'Card / Stripe', subtitle: 'International cards', icon: Card },
]

const GoldStar = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill="#F5A623" />
  </svg>
)

const formatBookingDate = (value) => {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  const day = date.toLocaleDateString('en-GB', { day: '2-digit' })
  const month = date.toLocaleDateString('en-GB', { month: 'short' })
  const year = date.getFullYear()
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
  return `${day} ${month} ${year}, ${weekday}`
}

const getNightCount = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 1
  const start = new Date(`${checkIn}T00:00:00`)
  const end = new Date(`${checkOut}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)))
}

/** Pull payment / checkout fields from hotel booking API shapes */
const extractPaymentInfo = (bookingResult = {}) => {
  const root = bookingResult || {}
  const candidates = [
    root,
    root.checkout,
    root.payment,
    root.payment_intent,
    root.stripe,
    root.data,
    root.data?.checkout,
    root.data?.payment,
    root.data?.payment_intent,
  ].filter(Boolean)

  let clientSecret = ''
  let amount = null
  let currency = ''
  let paymentIntentId = ''
  let publishableKey = ''
  let gateway = ''
  let orderId = ''
  let razorpayKey = ''
  let checkoutId = ''
  let requiresAction = false

  candidates.forEach((item) => {
    if (!clientSecret) {
      clientSecret = item.client_secret
        || item.clientSecret
        || item.payment_intent_client_secret
        || item.stripe_client_secret
        || item.secret
        || item.paymentIntent?.client_secret
        || item.payment_intent?.client_secret
        || ''
    }
    if (amount == null && item.amount != null) amount = item.amount
    if (!currency && item.currency) currency = item.currency
    if (!paymentIntentId) {
      paymentIntentId = item.payment_intent_id
        || (item.object === 'payment_intent' ? item.id : '')
        || ''
    }
    if (!publishableKey) {
      publishableKey = item.publishable_key
        || item.stripe_publishable_key
        || item.public_key
        || item.publishableKey
        || ''
    }
    if (!gateway) gateway = String(item.gateway || '').toLowerCase()
    if (!orderId) orderId = item.order_id || item.razorpay_order_id || ''
    if (!razorpayKey) razorpayKey = item.key || item.razorpay_key || ''
    if (!checkoutId) checkoutId = item.checkout_id || ''
    if (item.requires_action) requiresAction = true
  })

  if (root.requires_action) requiresAction = true

  const bookingId = root.booking_id
    || root.id
    || root.reference_id
    || root.booking_reference
    || root.data?.booking_id
    || root.data?.id
    || null

  // booking already paid / created with no further gateway step
  const bookingCreated = root.booking_created === true
    || Boolean(root.booking_id || root.id) && !clientSecret && !orderId

  return {
    clientSecret: String(clientSecret || '').trim(),
    amount,
    currency: currency || root.currency || root.requiredCurrency || '',
    paymentIntentId,
    publishableKey: String(publishableKey || '').trim(),
    bookingId,
    gateway: gateway || (clientSecret ? 'stripe' : (orderId ? 'razorpay' : '')),
    orderId: String(orderId || '').trim(),
    razorpayKey: String(razorpayKey || '').trim(),
    checkoutId: String(checkoutId || root.checkout_id || '').trim(),
    requiresAction,
    bookingCreated,
  }
}

/** Pull payment_summary block from hotel booking API response */
const extractPaymentSummary = (bookingResult = {}) => {
  const root = bookingResult || {}
  const candidates = [
    root.payment_summary,
    root.paymentSummary,
    root.fare_summary,
    root.data?.payment_summary,
    root.data?.paymentSummary,
    root.checkout?.payment_summary,
    root.payment?.payment_summary,
  ].filter(Boolean)

  const raw = candidates.find((item) => item && typeof item === 'object') || null
  if (!raw) return null

  const taxes = Array.isArray(raw.taxes)
    ? raw.taxes.map((tax) => ({
      name: tax?.name || tax?.tax_name || tax?.label || 'Tax',
      type: tax?.type || '',
      rate: tax?.rate != null ? Number(tax.rate) : null,
      amount: tax?.amount != null ? Number(tax.amount) : null,
    }))
    : []

  return {
    roomPrice: raw.room_price != null ? Number(raw.room_price) : (raw.roomPrice != null ? Number(raw.roomPrice) : null),
    taxes,
    taxAmount: raw.tax_amount != null ? Number(raw.tax_amount) : (raw.taxAmount != null ? Number(raw.taxAmount) : null),
    convenienceFee: raw.convenience_fee != null
      ? Number(raw.convenience_fee)
      : (raw.convenienceFee != null ? Number(raw.convenienceFee) : null),
    discount: raw.discount != null ? Number(raw.discount) : null,
    total: raw.total != null ? Number(raw.total) : (raw.grand_total != null ? Number(raw.grand_total) : null),
    currency: String(raw.currency || '').toUpperCase() || '',
  }
}

const flattenGuests = (bookingGuests) => {
  const rooms = bookingGuests?.rooms || bookingGuests || {}
  const guests = []
  Object.keys(rooms)
    .sort((a, b) => Number(a) - Number(b))
    .forEach((roomKey) => {
      const room = rooms[roomKey] || {}
      ;[...(room.adult || []), ...(room.child || [])].forEach((guest) => {
        guests.push(guest)
      })
    })
  return guests
}

const HotelPayment = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const searchParams = useSelector((state) => state.hotelSearch.searchParams)
  const selectedHotel = useSelector((state) => state.hotelSearch.selectedHotel)
  const hotelDetail = useSelector((state) => state.hotelSearch.hotelDetail)
  const selectedRoom = useSelector((state) => state.hotelSearch.selectedRoom)
  const bookingGuests = useSelector((state) => state.hotelSearch.bookingGuests)
  const bookingResult = useSelector((state) => state.hotelSearch.bookingResult)

  const [fareSummaryOpen, setFareSummaryOpen] = useState(true)
  const [secondsLeft, setSecondsLeft] = useState(9 * 60 + 29)
  const [paying, setPaying] = useState(false)
  const [gateway, setGateway] = useState(null)
  const [gatewayNotConfigured, setGatewayNotConfigured] = useState(false)
  const [stripeCardError, setStripeCardError] = useState('')
  const [stripeReady, setStripeReady] = useState(false)

  const stripeRef = useRef(null)
  const elementsRef = useRef(null)
  const paymentElementRef = useRef(null)
  const cardNumberRef = useRef(null)
  const cardExpiryRef = useRef(null)
  const cardCvcRef = useRef(null)
  const mountTokenRef = useRef(0)

  const paymentInfo = useMemo(() => {
    const info = extractPaymentInfo(bookingResult)
    if (!info.publishableKey) {
      info.publishableKey = findStripePublishableKeyInPayload(bookingResult)
    }
    return info
  }, [bookingResult])

  const paymentSummary = useMemo(
    () => extractPaymentSummary(bookingResult),
    [bookingResult]
  )

  // Resolve every render so a hot-reload after .env change can pick the key up.
  const stripeKey = getStripePublishableKey() || paymentInfo.publishableKey
  const razorpayKeyId = getRazorpayKeyId() || paymentInfo.razorpayKey
  const usePaymentElement = Boolean(paymentInfo.clientSecret)

  const hotelInfo = hotelDetail?.hotelInfo || null
  const option = selectedRoom?.option || null
  const roomsCount = Math.max(1, Number(searchParams.rooms) || 1)
  const adults = Number(searchParams.adults) || 1
  const children = Number(searchParams.children) || 0
  const nights = getNightCount(searchParams.checkIn, searchParams.checkOut)
  const currency = String(
    paymentSummary?.currency
    || paymentInfo.currency
    || option?.currency
    || searchParams.requiredCurrency
    || selectedHotel?.currency
    || 'INR'
  ).toUpperCase()

  const packageRoomPrice = Number(option?.rawPrice)
  // Rate from hotel room API is priced for full occupancy (all rooms); don't multiply again.
  const totalFromRoom = !Number.isNaN(packageRoomPrice) ? packageRoomPrice : null
  const unitRoomPrice = totalFromRoom != null && roomsCount > 0
    ? totalFromRoom / roomsCount
    : packageRoomPrice
  // Prefer booking payment_summary.total for payable amount (includes tax + convenience)
  const amountToPay = paymentSummary?.total != null && !Number.isNaN(paymentSummary.total)
    ? paymentSummary.total
    : (paymentInfo.amount != null
      ? Number(paymentInfo.amount)
      : totalFromRoom)
  const amountLabel = amountToPay != null && !Number.isNaN(amountToPay)
    ? formatMoney(amountToPay, currency)
    : (option?.price || '—')

  const formatFare = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—'
    return formatMoney(Number(value), currency)
  }

  /**
   * Prefer payment_summary totals from booking API (authoritative).
   * Unit room = package / rooms when rate is occupancy package, or payment_summary / rooms.
   */
  const fareBreakdown = useMemo(() => {
    if (!paymentSummary) return null
    const rooms = roomsCount
    const roomTotal = paymentSummary.roomPrice
    let unitRoom = null
    // Prefer package/rate unit when it divides cleanly into payment room total
    if (!Number.isNaN(packageRoomPrice) && packageRoomPrice > 0 && rooms > 0) {
      if (roomTotal != null && Math.abs(packageRoomPrice - roomTotal) < 1) {
        // option rawPrice already equals payment room total (package for all rooms)
        unitRoom = packageRoomPrice / rooms
      } else if (roomTotal != null && Math.abs((packageRoomPrice / rooms) * rooms - roomTotal) < 1) {
        unitRoom = packageRoomPrice / rooms
      } else if (roomTotal != null && Math.abs(packageRoomPrice * rooms - roomTotal) < 1) {
        // option rawPrice is true per-room unit
        unitRoom = packageRoomPrice
      }
    }
    if (unitRoom == null && roomTotal != null && rooms > 0) {
      unitRoom = roomTotal / rooms
    }

    return {
      rooms,
      unitRoom,
      roomTotal,
      taxes: paymentSummary.taxes || [],
      convenienceFee: paymentSummary.convenienceFee,
      discount: paymentSummary.discount,
      total: paymentSummary.total,
      currency: paymentSummary.currency || currency,
    }
  }, [paymentSummary, roomsCount, packageRoomPrice, currency])

  const hotelName = hotelInfo?.name || selectedHotel?.name || selectedRoom?.hotelName || 'Hotel'
  const hotelRating = Number(hotelInfo?.rating ?? selectedHotel?.rating ?? 0)
  const starCount = Number.isNaN(hotelRating) ? 0 : Math.max(0, Math.min(5, Math.round(hotelRating)))
  const address = hotelInfo?.address || selectedHotel?.address || {}
  const locationLabel = [address.city, address.country || address.address]
    .filter(Boolean)
    .join(', ')
    || selectedHotel?.location
    || address.address
    || '—'

  const guests = useMemo(() => flattenGuests(bookingGuests), [bookingGuests])
  const guestNames = guests.map((g) => [g.first_name, g.last_name].filter(Boolean).join(' ').trim()).filter(Boolean)
  const primaryGuest = guests[0] || {}
  const guestsRoomsLabel = [
    `${adults} Adult${adults > 1 ? 's' : ''}`,
    children > 0 ? `${children} Child${children > 1 ? 'ren' : ''}` : null,
    `${roomsCount} Room${roomsCount > 1 ? 's' : ''}`,
  ].filter(Boolean).join(' • ')

  // Prefer API checkout gateway when present; else same currency rule as flights.
  const allowedKeys = useMemo(() => {
    if (paymentInfo.gateway) return [paymentInfo.gateway]
    // If booking already returned a Stripe secret, keep card flow available even for INR.
    if (paymentInfo.clientSecret) return ['stripe']
    if (paymentInfo.orderId) return ['razorpay']
    return resolveAllowedGateways(currency)
  }, [paymentInfo.gateway, paymentInfo.clientSecret, paymentInfo.orderId, currency])

  const availableGateways = useMemo(
    () => GATEWAYS.filter((g) => allowedKeys.includes(g.key)),
    [allowedKeys]
  )

  useEffect(() => {
    if (!availableGateways.length) return
    if (!gateway || !availableGateways.some((g) => g.key === gateway)) {
      setGateway(availableGateways[0].key)
    }
  }, [availableGateways, gateway])

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timerId = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000)
    return () => clearTimeout(timerId)
  }, [secondsLeft])

  const destroyStripeElements = () => {
    ;[paymentElementRef, cardNumberRef, cardExpiryRef, cardCvcRef].forEach((ref) => {
      if (ref.current) {
        try {
          ref.current.unmount()
        } catch {
          // ignore unmount races
        }
        ref.current = null
      }
    })
    elementsRef.current = null
    setStripeReady(false)
  }

  const stripeFieldStyle = {
    base: {
      fontSize: '16px',
      color: '#1D2A4D',
      fontFamily: 'Onest, system-ui, sans-serif',
      '::placeholder': { color: '#9CA3AF' },
    },
    invalid: { color: '#E23744' },
  }

  // Mount full Stripe UI:
  // - Payment Element when booking returned client_secret (full card UI)
  // - Split Card Number / Expiry / CVC otherwise (clear labeled fields)
  useEffect(() => {
    if (gateway !== 'stripe') {
      destroyStripeElements()
      return undefined
    }

    let cancelled = false
    const token = ++mountTokenRef.current

    const mountStripe = async () => {
      setStripeReady(false)
      setStripeCardError('')

      if (!stripeKey) {
        setStripeCardError(
          'Stripe publishable key not loaded. Stop the app, then run npm start again so .env REACT_APP_STRIPE_PUBLISHABLE_KEY is picked up.'
        )
        return
      }

      try {
        // Always re-init with current key (covers key change after env reload)
        const Stripe = await loadScript('https://js.stripe.com/v3/', 'Stripe')
        if (cancelled || token !== mountTokenRef.current) return
        stripeRef.current = Stripe(stripeKey)

        destroyStripeElements()
        if (cancelled || token !== mountTokenRef.current) return

        // Wait for mount nodes after React paints the stripe panel
        await new Promise((r) => window.setTimeout(r, 50))
        if (cancelled || token !== mountTokenRef.current) return

        if (usePaymentElement) {
          const mountEl = document.getElementById('hotel-payment-element')
          if (!mountEl) {
            if (!cancelled) setStripeCardError('Stripe form container not ready. Refresh and try again.')
            return
          }
          // Clear previous iframe remnants so Stripe can remount cleanly
          mountEl.innerHTML = ''
          const elements = stripeRef.current.elements({
            clientSecret: paymentInfo.clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#026D4D',
                borderRadius: '12px',
                fontFamily: 'Onest, system-ui, sans-serif',
              },
            },
          })
          elementsRef.current = elements
          const paymentElement = elements.create('payment', {
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
            paymentMethodOrder: ['card'],
            fields: {
              billingDetails: 'auto',
            },
          })
          paymentElement.mount('#hotel-payment-element')
          paymentElement.on('change', (event) => {
            if (!cancelled) setStripeCardError(event.error ? event.error.message : '')
          })
          paymentElement.on('ready', () => {
            if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
          })
          paymentElementRef.current = paymentElement
        } else {
          const numberEl = document.getElementById('hotel-card-number')
          const expiryEl = document.getElementById('hotel-card-expiry')
          const cvcEl = document.getElementById('hotel-card-cvc')
          if (!numberEl || !expiryEl || !cvcEl) {
            if (!cancelled) setStripeCardError('Stripe form container not ready. Refresh and try again.')
            return
          }
          numberEl.innerHTML = ''
          expiryEl.innerHTML = ''
          cvcEl.innerHTML = ''
          const elements = stripeRef.current.elements()
          elementsRef.current = elements
          const number = elements.create('cardNumber', { style: stripeFieldStyle, showIcon: true })
          const expiry = elements.create('cardExpiry', { style: stripeFieldStyle })
          const cvc = elements.create('cardCvc', { style: stripeFieldStyle })
          number.mount('#hotel-card-number')
          expiry.mount('#hotel-card-expiry')
          cvc.mount('#hotel-card-cvc')
          number.on('change', (event) => {
            if (!cancelled) setStripeCardError(event.error ? event.error.message : '')
          })
          number.on('ready', () => {
            if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
          })
          cardNumberRef.current = number
          cardExpiryRef.current = expiry
          cardCvcRef.current = cvc
        }

        // Fallback ready if 'ready' event is slow/missed
        if (!cancelled && token === mountTokenRef.current) {
          window.setTimeout(() => {
            if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
          }, 800)
        }
      } catch (error) {
        if (!cancelled) {
          setStripeCardError(error?.message || 'Failed to load Stripe card form.')
          setStripeReady(false)
        }
      }
    }

    mountStripe()

    return () => {
      cancelled = true
      destroyStripeElements()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, stripeKey, usePaymentElement, paymentInfo.clientSecret])

  const createStripePaymentMethod = async () => {
    if (!stripeRef.current || !cardNumberRef.current) {
      throw new Error('Stripe card form failed to initialize.')
    }
    const name = [primaryGuest.first_name, primaryGuest.last_name].filter(Boolean).join(' ').trim()
      || guestNames[0]
      || 'Guest'
    const result = await stripeRef.current.createPaymentMethod({
      type: 'card',
      card: cardNumberRef.current,
      billing_details: {
        name,
        email: primaryGuest.email || '',
        phone: primaryGuest.phone || '',
      },
    })
    if (result.error) throw new Error(result.error.message || 'Could not create Stripe payment method.')
    if (!result.paymentMethod?.id) throw new Error('Stripe payment method id missing.')
    return result.paymentMethod
  }

  /** 3DS / authentication modal — same as flight payWithStripe3DS */
  const payWithStripe3DS = async (clientSecret) => {
    if (!stripeRef.current) throw new Error('Stripe SDK failed to initialize.')
    if (!clientSecret) throw new Error('Stripe client_secret missing for 3DS.')
    const result = await stripeRef.current.handleCardAction(clientSecret)
    if (result.error) throw new Error(result.error.message || 'Card authentication failed.')
    const intent = result.paymentIntent
    if (!intent) throw new Error('Stripe payment intent missing after authentication.')
    return intent
  }

  const completeBooking = (extra = {}) => {
    dispatch(setHotelBookingConfirmation({
      ...bookingResult,
      ...extra,
      paidAt: new Date().toISOString(),
    }))
    navigate('/hotel-booking-confirm')
  }

  const payWithRazorpay = async () => {
    const Razorpay = await loadScript('https://checkout.razorpay.com/v1/checkout.js', 'Razorpay')
    const key = String(paymentInfo.razorpayKey || razorpayKeyId || '').trim()
    const orderId = String(paymentInfo.orderId || '').trim()
    if (!key) throw new Error('Razorpay key is missing from checkout response.')
    if (!orderId) throw new Error('Razorpay order_id missing from checkout response.')

    const name = guestNames[0] || 'Guest'
    const email = primaryGuest.email || ''
    const phoneDigits = String(primaryGuest.phone || '').replace(/\D/g, '')
    const prefill = { name }
    if (email) prefill.email = email
    if (phoneDigits.length >= 10 && phoneDigits.length <= 15) prefill.contact = phoneDigits.slice(-10)

    return new Promise((resolve, reject) => {
      let settled = false
      const finish = (fn, value) => {
        if (settled) return
        settled = true
        fn(value)
      }
      try {
        const rzp = new Razorpay({
          key,
          name: 'TravelGo',
          description: 'Hotel booking',
          order_id: orderId,
          prefill,
          theme: { color: '#15966f' },
          handler(response) {
            finish(resolve, response)
          },
          modal: {
            ondismiss() {
              if (!settled) finish(reject, new Error('Razorpay closed before payment finished.'))
            },
          },
        })
        rzp.on('payment.failed', (response) => {
          const description = response?.error?.description || response?.error?.reason || 'Razorpay payment failed.'
          finish(reject, new Error(description))
        })
        window.setTimeout(() => {
          try {
            rzp.open()
          } catch (error) {
            finish(reject, error instanceof Error ? error : new Error(String(error)))
          }
        }, 0)
      } catch (error) {
        finish(reject, error instanceof Error ? error : new Error(String(error)))
      }
    })
  }

  const payWithStripe = async () => {
    if (!stripeRef.current) throw new Error('Stripe SDK failed to initialize.')
    if (!stripeReady) throw new Error('Stripe card form is still loading.')

    // Full Payment Element path (booking API already created PaymentIntent)
    if (usePaymentElement && elementsRef.current && paymentInfo.clientSecret) {
      const result = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/hotel-booking-confirm`,
        },
      })
      if (result.error) throw new Error(result.error.message || 'Payment failed.')
      const intent = result.paymentIntent
      if (!intent || !['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
        throw new Error(`Stripe status: ${intent?.status || 'unknown'}`)
      }
      completeBooking({
        paymentMethod: 'stripe',
        paymentIntent: intent,
        payment_intent_id: intent.id,
      })
      return
    }

    // Split Card Element path — create PM then confirm if secret exists
    const paymentMethod = await createStripePaymentMethod()

    if (!paymentInfo.clientSecret) {
      if (paymentInfo.bookingCreated || paymentInfo.bookingId) {
        completeBooking({
          paymentMethod: 'stripe',
          payment_method_id: paymentMethod.id,
        })
        return
      }
      throw new Error('Stripe client_secret missing from hotel booking response.')
    }

    const confirmResult = await stripeRef.current.confirmCardPayment(paymentInfo.clientSecret, {
      payment_method: paymentMethod.id,
    })

    if (confirmResult.error) {
      if (paymentInfo.requiresAction || /authenticate|requires.?action/i.test(confirmResult.error.message || '')) {
        const intent = await payWithStripe3DS(paymentInfo.clientSecret)
        if (!['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
          throw new Error(`Stripe status: ${intent.status || 'unknown'}`)
        }
        completeBooking({
          paymentMethod: 'stripe',
          paymentIntent: intent,
          payment_intent_id: intent.id,
        })
        return
      }
      throw new Error(confirmResult.error.message || 'Payment failed.')
    }

    const intent = confirmResult.paymentIntent
    if (!intent || !['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
      throw new Error(`Stripe status: ${intent?.status || 'unknown'}`)
    }

    completeBooking({
      paymentMethod: 'stripe',
      paymentIntent: intent,
      payment_intent_id: intent.id,
    })
  }

  const handlePayClick = async () => {
    if (paying) return
    if (!gateway) {
      showToast('Please choose a payment method', 'error')
      return
    }
    const isConfigured = gateway === 'razorpay'
      ? !!(paymentInfo.razorpayKey || razorpayKeyId)
      : !!stripeKey
    if (!isConfigured) {
      setGatewayNotConfigured(true)
      return
    }

    setPaying(true)
    setGatewayNotConfigured(false)
    try {
      if (gateway === 'stripe') {
        await payWithStripe()
        showToast('Payment successful', 'success')
        return
      }
      if (gateway === 'razorpay') {
        const response = await payWithRazorpay()
        completeBooking({
          paymentMethod: 'razorpay',
          razorpay: response,
        })
        showToast('Payment successful', 'success')
        return
      }
      showToast('Select Razorpay or Stripe.', 'error')
    } catch (error) {
      showToast(error?.message || 'Payment failed.', 'error')
    } finally {
      setPaying(false)
    }
  }

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
  const seconds = String(secondsLeft % 60).padStart(2, '0')

  if (!bookingResult) {
    return (
      <section className="paymentPart commonTopPadding commonBottomPadding">
        <div className="container">
          <p>No hotel booking in progress. Please complete guest details first.</p>
          <button type="button" className="commonBtn" onClick={() => navigate('/hotel-booking-detail')}>
            Back to guest details
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="paymentPart commonTopPadding commonBottomPadding">
      <div className="container">
        <div className="row g-4">
          <div className="col-md-12">
            <div className="time bgWhite paddingCommon20 borderRadius40 commonBorder">
              <span className="timePill">
                {minutes} : {seconds}
              </span>
            </div>
          </div>
          <div className="col-md-12">
            <div className="paymentSec allBookingDetail">
              <div className="onlineCrad allBokkingInfo">
                <div className="bsSectionTitle">Payment</div>
                <p className="bsFareNote">
                  Choose a payment method to complete your booking.
                </p>

                <div className="paymentMethodBox">
                  <div className="paymentMethodList">
                    {availableGateways.map((gw) => (
                      <div
                        key={gw.key}
                        className={`paymentMethodItem ${gateway === gw.key ? 'active' : ''}`}
                        onClick={() => {
                          setGatewayNotConfigured(false)
                          setGateway(gw.key)
                        }}
                      >
                        <span className="paymentMethodIcon">
                          <img src={gw.icon} alt={gw.label} />
                        </span>
                        <div>
                          <div className="paymentMethodTitle">{gw.label}</div>
                          <div className="paymentMethodSub">{gw.subtitle}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="paymentMethodPanel">
                    {gateway === 'stripe' ? (
                      <>
                        <div className="bsSectionTitle">Enter your card details</div>
                        {stripeKey ? (
                          <div className="paymentStripeCardWrap">
                            {usePaymentElement ? (
                              <div id="hotel-payment-element" className="paymentStripeCard" />
                            ) : (
                              <div className="paymentStripeFields">
                                <div className="paymentStripeField">
                                  <label htmlFor="hotel-card-number">Card number</label>
                                  <div id="hotel-card-number" className="paymentStripeCardField" />
                                </div>
                                <div className="paymentStripeFields__row">
                                  <div className="paymentStripeField">
                                    <label htmlFor="hotel-card-expiry">Expiry</label>
                                    <div id="hotel-card-expiry" className="paymentStripeCardField" />
                                  </div>
                                  <div className="paymentStripeField">
                                    <label htmlFor="hotel-card-cvc">CVC</label>
                                    <div id="hotel-card-cvc" className="paymentStripeCardField" />
                                  </div>
                                </div>
                              </div>
                            )}
                            {stripeCardError && <p className="bsFareNote">{stripeCardError}</p>}
                            {!stripeReady && !stripeCardError && (
                              <HotelSearchLoader size='compact' text='Loading secure card form' />
                            )}
                          </div>
                        ) : (
                          <div className="paymentInfoBanner">
                            Stripe key not loaded. Stop the dev server fully, then run{' '}
                            <code>npm start</code> again so{' '}
                            <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> from <code>.env</code> is available.
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="bsSectionTitle">Pay via Razorpay</div>
                        <p className="paymentQrDesc">
                          UPI, cards, net banking and wallets — pick your preferred option in the next step.
                        </p>
                      </>
                    )}

                    {gatewayNotConfigured && (
                      <div className="paymentInfoBanner">
                        Payment gateway not configured in this environment. Add
                        {' '}
                        {gateway === 'razorpay' ? 'REACT_APP_RAZORPAY_KEY_ID' : 'REACT_APP_STRIPE_PUBLISHABLE_KEY'}
                        {' '}
                        to `.env` and restart the app.
                      </div>
                    )}

                    <button
                      type="button"
                      className="commonBtn w-100"
                      onClick={handlePayClick}
                      disabled={paying || (gateway === 'stripe' && !stripeReady)}
                    >
                      {paying ? (
                        <>
                          <span className='btnSpinner' aria-hidden="true" />
                          Processing…
                        </>
                      ) : (
                        `Pay ${amountLabel}`
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="paymentSidebar bookingSidebar">
                <div className="bsSection bgWhite paddingCommon20 borderRadius20 commonBorder">
                  <div className="fareDetailsHead" onClick={() => setFareSummaryOpen(!fareSummaryOpen)}>
                    <div className="bsSectionTitle">Fare Summary</div>
                    <span className={`fareCollapseIcon ${fareSummaryOpen ? 'open' : ''}`}>&#8963;</span>
                  </div>
                  {fareSummaryOpen && (
                    <>
                      {fareBreakdown ? (
                        <>
                          {fareBreakdown.rooms > 1 && fareBreakdown.unitRoom != null ? (
                            <div className="bsFareRow mt-3">
                              <span>
                                Room Price
                                <span className="bsFareNote" style={{ display: 'block', margin: 0 }}>
                                  {formatFare(fareBreakdown.unitRoom)} × {fareBreakdown.rooms} rooms
                                </span>
                              </span>
                              <span>{formatFare(fareBreakdown.roomTotal)}</span>
                            </div>
                          ) : (
                            <div className="bsFareRow mt-3">
                              <span>Room Price{fareBreakdown.rooms > 1 ? ` (${fareBreakdown.rooms} rooms)` : ''}</span>
                              <span>{formatFare(fareBreakdown.roomTotal)}</span>
                            </div>
                          )}

                          {(fareBreakdown.taxes || []).map((tax, index) => {
                            const details = [
                              tax.type ? String(tax.type) : null,
                              tax.rate != null && !Number.isNaN(tax.rate)
                                ? (tax.type === 'percentage' ? `${tax.rate}%` : String(tax.rate))
                                : null,
                            ].filter(Boolean).join(' · ')
                            const taxUnit = fareBreakdown.rooms > 1 && tax.amount != null
                              ? Number(tax.amount) / fareBreakdown.rooms
                              : null
                            return (
                              <div className="bsFareRow" key={`tax-${tax.name}-${index}`}>
                                <span>
                                  {tax.name || 'Tax'}
                                  {details ? ` (${details})` : ''}
                                  {taxUnit != null && fareBreakdown.rooms > 1 ? (
                                    <span className="bsFareNote" style={{ display: 'block', margin: 0 }}>
                                      {formatFare(taxUnit)} × {fareBreakdown.rooms} rooms
                                    </span>
                                  ) : null}
                                </span>
                                <span>{formatFare(tax.amount)}</span>
                              </div>
                            )
                          })}

                          <div className="bsFareRow">
                            <span>Convenience Fee</span>
                            <span>{formatFare(fareBreakdown.convenienceFee)}</span>
                          </div>

                          <div className="bsFareRow">
                            <span>Discount</span>
                            <span>
                              {fareBreakdown.discount != null && Number(fareBreakdown.discount) > 0
                                ? `-${formatFare(fareBreakdown.discount)}`
                                : formatFare(fareBreakdown.discount ?? 0)}
                            </span>
                          </div>

                          {fareBreakdown.rooms > 1 ? (
                            <p className="bsFareNote mt-2">
                              Prices above are for all {fareBreakdown.rooms} rooms. Convenience fee is charged once.
                            </p>
                          ) : null}

                          <div className="bsFareDivider" />
                          <div className="bsFareRow bsFareRow--total">
                            <span>Amount To Be Paid</span>
                            <span>{formatFare(fareBreakdown.total ?? amountToPay)}</span>
                          </div>
                          {fareBreakdown.currency ? (
                            <p className="bsFareNote">Currency: {fareBreakdown.currency}</p>
                          ) : null}
                        </>
                      ) : (
                        <div className="bsFareRow bsFareRow--total mt-3">
                          <span>Amount To Be Paid</span>
                          <span>{amountLabel}</span>
                        </div>
                      )}
                    </>
                  )}
                  {paymentInfo.bookingId && (
                    <p className="bsFareNote">Booking ref: {paymentInfo.bookingId}</p>
                  )}
                </div>

                <div className="bsSection">
                  <div className="bsBox">
                    <div className="hotelCard__topRow">
                      <div>
                        <div className="hotelCard__name">{hotelName}</div>
                        <div className="hotelCard__location mt-2">
                          <img src={Location} alt="" />
                          <div className="hotelCard__locationName">{locationLabel}</div>
                        </div>
                      </div>
                      <div className="hotelCard__stars">
                        {Array.from({ length: starCount }).map((_, index) => (
                          <GoldStar key={index} />
                        ))}
                      </div>
                    </div>

                    <div className="bookingSegmentBox bookingInfoRow">
                      <div>
                        <div className="bookingInfoLabel">CHECK-IN</div>
                        <div className="bookingInfoDate">{formatBookingDate(searchParams.checkIn)}</div>
                      </div>
                      <div className="bookingInfoNights">— {nights} Night{nights > 1 ? 's' : ''} —</div>
                      <div className="text-end">
                        <div className="bookingInfoLabel">CHECK-OUT</div>
                        <div className="bookingInfoDate">{formatBookingDate(searchParams.checkOut)}</div>
                      </div>
                    </div>

                    <div className="bsFareRow mt-2">
                      <span>GUEST &amp; ROOMS</span>
                      <span>{guestsRoomsLabel}</span>
                    </div>

                    <div className="bsFareDivider" />

                    <div className="bsSectionTitle fareBaggageTitle">Guests</div>
                    <ul className="paymentTravellerList">
                      {guestNames.length ? guestNames.map((name, index) => (
                        <li key={`${name}-${index}`}>{index + 1}. {name}</li>
                      )) : (
                        <li>Guest details unavailable</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HotelPayment
